import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { ShopifyBadge, MicrosoftBadge } from './PartnerBadges';

const capabilities = [
  { id: '01', name: 'Shopify Expert Partner' },
  { id: '02', name: 'Cloud & DevOps Transformation' },
  { id: '03', name: 'Microsoft 365 & Azure Solutions' },
  { id: '04', name: 'Cybersecurity & Network Engineering' },
  { id: '05', name: 'IT Infrastructure & Data Center' },
  { id: '06', name: 'IT Consulting & Digital Transformation' },
];

export const HeroSection = () => {
  const ref = useRef(null);
  const auroraRef = useRef(null);
  const cardRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const y       = useTransform(scrollYProgress, [0, 0.6], [0, 50]);

  /* Mouse-reactive aurora — the Stripe signature */
  useEffect(() => {
    const onMove = (e) => {
      if (!ref.current || !auroraRef.current) return;
      const r = ref.current.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const yy = ((e.clientY - r.top) / r.height) * 100;
      auroraRef.current.style.setProperty('--mx', `${x}%`);
      auroraRef.current.style.setProperty('--my', `${yy}%`);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  /* Tilt for capability card */
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotX = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 150, damping: 20 });
  const rotY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 150, damping: 20 });

  const onCardMove = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onCardLeave = () => { mx.set(0.5); my.set(0.5); };

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden stripe-wash-hero"
    >
      {/* Mouse-reactive aurora — single Royal Blue glow */}
      <div ref={auroraRef} className="stripe-aurora" />

      <motion.div style={{ opacity, y }} className="container-custom pt-36 pb-24 w-full relative z-10">
        <div className="grid lg:grid-cols-[1fr_440px] gap-16 items-center">

          {/* LEFT */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-10"
            >
              <span className="section-label">Kuwait City, Kuwait</span>
              <span className="w-8 h-px bg-[#2563EB]/30" />
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] pulse-dot" />
                <span className="font-mono text-[0.6rem] tracking-[0.2em] text-[#425466] uppercase font-medium">
                  Available Now
                </span>
              </div>
            </motion.div>

            <div className="overflow-hidden mb-1">
              <motion.h1
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-bold text-[#0F172A] leading-[0.95]"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.25rem)' }}
              >
                Smart IT
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-1">
              <motion.h1
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-bold italic text-gold leading-[0.95]"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.25rem)' }}
              >
                Solutions
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-9">
              <motion.h1
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-bold text-[#0F172A] leading-[0.95]"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.25rem)' }}
              >
                for Modern Business
              </motion.h1>
            </div>

            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.42, ease: 'easeOut' }}
              className="divider-gold origin-left mb-9"
            />

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.52 }}
              className="text-[#425466] text-lg max-w-lg leading-relaxed font-normal mb-9"
            >
              Certified <strong className="text-[#0F172A] font-semibold">Shopify Expert</strong> &{' '}
              <strong className="text-[#0F172A] font-semibold">Microsoft Partner</strong> — delivering
              cloud infrastructure, cybersecurity, enterprise networking, and e-commerce solutions across Kuwait and the GCC.
            </motion.p>

            {/* Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.62 }}
              className="flex flex-wrap gap-3 mb-9"
            >
              <ShopifyBadge />
              <MicrosoftBadge />
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.72 }}
              className="flex flex-wrap gap-4"
            >
              <button
                onClick={() => scrollTo('#contact')}
                className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2563EB] text-white text-sm font-semibold transition-all duration-300 shadow-[0_4px_14px_rgba(37,99,235,0.4)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.55)] hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#2563EB] via-[#3B82F6] to-[#2563EB] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative flex items-center gap-2">
                  Get Free Consultation
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <button
                onClick={() => scrollTo('#services')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#E3E8EE] text-[#425466] text-sm font-medium hover:border-[#2563EB] hover:text-[#2563EB] hover:bg-white transition-all duration-300 bg-white/60 backdrop-blur-sm"
              >
                Our Services
              </button>
            </motion.div>
          </div>

          {/* RIGHT — Tilting capability card */}
          <motion.div
            ref={cardRef}
            onMouseMove={onCardMove}
            onMouseLeave={onCardLeave}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1200, transformStyle: 'preserve-3d' }}
            className="hidden lg:block relative"
          >
            {/* Soft glow behind card */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-[#2563EB]/15 via-[#2563EB]/15 to-[#93C5FD]/15 blur-2xl pointer-events-none" />

            <div className="relative bg-white rounded-2xl border border-[#E3E8EE] shadow-[0_20px_60px_rgba(15,23,42,0.08)] overflow-hidden" style={{ transform: 'translateZ(40px)' }}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E8EE] bg-gradient-to-r from-[#F6F9FC] to-white">
                <span className="font-mono text-[0.6rem] tracking-[0.22em] text-[#425466] uppercase font-medium">
                  Core Capabilities
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] pulse-dot" />
                  <span className="font-mono text-[0.55rem] tracking-widest text-[#2563EB] font-semibold">LIVE</span>
                </div>
              </div>

              {capabilities.map((cap, i) => (
                <motion.div
                  key={cap.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.08 }}
                  className="group relative px-6 py-4 border-b border-[#E3E8EE] last:border-0 cursor-default overflow-hidden hover:bg-[#F6F9FC] transition-colors duration-300"
                >
                  <div className="relative flex items-center gap-4">
                    <span className="font-mono text-[0.58rem] tracking-widest text-[#8898AA] w-5 shrink-0 group-hover:text-[#2563EB] transition-colors duration-300">
                      {cap.id}
                    </span>
                    <span className="text-[0.85rem] text-[#0F172A] font-medium flex-1">
                      {cap.name}
                    </span>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center bg-[#EFF6FF] group-hover:bg-[#2563EB] transition-colors duration-300 shrink-0">
                      <Check size={11} className="text-[#2563EB] group-hover:text-white transition-colors duration-300" strokeWidth={3} />
                    </span>
                  </div>
                </motion.div>
              ))}

              <div className="px-6 py-4 border-t border-[#E3E8EE] bg-gradient-to-r from-[#EFF6FF] via-[#EFF6FF] to-[#EFF6FF] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[0.58rem] tracking-widest text-[#425466] uppercase font-medium">Response Time</span>
                  <span className="font-mono text-[0.58rem] text-[#2563EB] font-semibold">&lt; 24H</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[0.58rem] tracking-widest text-[#425466] uppercase font-medium">Consultation</span>
                  <span className="font-mono text-[0.58rem] text-[#2563EB] font-semibold">FREE</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <span className="font-mono text-[0.55rem] tracking-[0.35em] text-[#8898AA] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-8 bg-gradient-to-b from-[#2563EB]/40 to-transparent"
        />
      </motion.div>
    </section>
  );
};
